import { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const WALL_TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/d75eefd7-e138-4cbb-9f59-667886564f01/images/1d5246089074b3976b4ec0279088e31e993ce3d3edfde0ee0cfc2b47dd13a03c.jpeg";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const WallCanvas = forwardRef(function WallCanvas(
  { gridSize, tool, soldSet, soldMap, selected, onToggle, onBoxSelect, onHover },
  ref
) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const view = useRef({ scale: 1, tx: 0, ty: 0, baseCell: 1 });
  const bgImg = useRef(null);
  const drag = useRef(null);
  const hoverCell = useRef(null);
  const rafId = useRef(null);
  const propsRef = useRef({});
  propsRef.current = { gridSize, tool, soldSet, soldMap, selected, onToggle, onBoxSelect, onHover };

  const requestDraw = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      draw();
    });
  }, []);

  const cellSize = () => view.current.baseCell * view.current.scale;

  const cellAt = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const c = cellSize();
    const cx = Math.floor((mx - view.current.tx) / c);
    const cy = Math.floor((my - view.current.ty) / c);
    return { cx, cy, mx, my };
  };

  const clampPan = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const g = propsRef.current.gridSize;
    const gridPx = cellSize() * g;
    const margin = Math.min(cw, ch) * 0.4;
    view.current.tx = clamp(view.current.tx, cw - gridPx - margin, margin);
    view.current.ty = clamp(view.current.ty, ch - gridPx - margin, margin);
  };

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);

    const { gridSize: g, soldSet: sold, soldMap: sMap, selected: sel } = propsRef.current;
    const c = cellSize();
    const { tx, ty } = view.current;
    const gridPx = c * g;

    // background
    ctx.fillStyle = "#0a0e17";
    ctx.fillRect(0, 0, cw, ch);
    if (bgImg.current && bgImg.current.complete) {
      ctx.drawImage(bgImg.current, tx, ty, gridPx, gridPx);
    } else {
      ctx.fillStyle = "#16223f";
      ctx.fillRect(tx, ty, gridPx, gridPx);
    }
    // dim overlay for readability
    ctx.fillStyle = "rgba(10,14,23,0.35)";
    ctx.fillRect(Math.max(0, tx), Math.max(0, ty), Math.min(gridPx, cw - tx), Math.min(gridPx, ch - ty));

    // visible cell range
    const startX = clamp(Math.floor(-tx / c), 0, g);
    const endX = clamp(Math.ceil((cw - tx) / c), 0, g);
    const startY = clamp(Math.floor(-ty / c), 0, g);
    const endY = clamp(Math.ceil((ch - ty) / c), 0, g);

    // sold pixels
    for (const idx of sold) {
      const x = idx % g;
      const y = Math.floor(idx / g);
      if (x < startX - 1 || x > endX || y < startY - 1 || y > endY) continue;
      const sx = x * c + tx;
      const sy = y * c + ty;
      drawHeart(ctx, sx, sy, c);
    }

    // selection
    ctx.fillStyle = "rgba(245,158,11,0.55)";
    ctx.strokeStyle = "#f59e0b";
    ctx.lineWidth = Math.min(2, c * 0.15);
    for (const idx of sel) {
      const x = idx % g;
      const y = Math.floor(idx / g);
      if (x < startX - 1 || x > endX || y < startY - 1 || y > endY) continue;
      const sx = x * c + tx;
      const sy = y * c + ty;
      ctx.fillRect(sx, sy, c, c);
      if (c > 5) ctx.strokeRect(sx + 0.5, sy + 0.5, c - 1, c - 1);
    }

    // grid lines
    if (c >= 7) {
      ctx.strokeStyle = "rgba(148,163,184,0.14)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = startX; x <= endX; x++) {
        const sx = Math.round(x * c + tx) + 0.5;
        ctx.moveTo(sx, Math.max(0, ty));
        ctx.lineTo(sx, Math.min(ch, ty + gridPx));
      }
      for (let y = startY; y <= endY; y++) {
        const sy = Math.round(y * c + ty) + 0.5;
        ctx.moveTo(Math.max(0, tx), sy);
        ctx.lineTo(Math.min(cw, tx + gridPx), sy);
      }
      ctx.stroke();
    }

    // active selection rectangle (drag)
    if (drag.current && drag.current.mode === "select" && drag.current.moved) {
      const a = drag.current.startCell;
      const b = drag.current.curCell;
      const minX = Math.min(a.cx, b.cx);
      const minY = Math.min(a.cy, b.cy);
      const w = (Math.abs(a.cx - b.cx) + 1) * c;
      const h = (Math.abs(a.cy - b.cy) + 1) * c;
      ctx.fillStyle = "rgba(59,130,246,0.25)";
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 1.5;
      ctx.fillRect(minX * c + tx, minY * c + ty, w, h);
      ctx.strokeRect(minX * c + tx, minY * c + ty, w, h);
    }

    // hover highlight
    const hc = hoverCell.current;
    if (hc && hc.cx >= 0 && hc.cx < g && hc.cy >= 0 && hc.cy < g) {
      const sx = hc.cx * c + tx;
      const sy = hc.cy * c + ty;
      const idx = hc.cy * g + hc.cx;
      ctx.strokeStyle = sold.has(idx) ? "#f43f5e" : "#38bdf8";
      ctx.lineWidth = 2;
      ctx.strokeRect(sx + 1, sy + 1, c - 2, c - 2);
    }
  }

  function drawHeart(ctx, sx, sy, c) {
    ctx.fillStyle = "#e11d48";
    ctx.fillRect(sx, sy, c, c);
    if (c >= 9) {
      const cx = sx + c / 2;
      const cy = sy + c / 2;
      const s = c * 0.34;
      ctx.fillStyle = "#fecdd3";
      ctx.beginPath();
      ctx.moveTo(cx, cy + s * 0.9);
      ctx.bezierCurveTo(cx + s, cy - s * 0.2, cx + s * 0.55, cy - s, cx, cy - s * 0.35);
      ctx.bezierCurveTo(cx - s * 0.55, cy - s, cx - s, cy - s * 0.2, cx, cy + s * 0.9);
      ctx.closePath();
      ctx.fill();
    }
  }

  const fitView = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const g = propsRef.current.gridSize;
    const base = Math.min(cw, ch) / g;
    view.current.baseCell = base;
    view.current.scale = 1;
    const gridPx = base * g;
    view.current.tx = (cw - gridPx) / 2;
    view.current.ty = (ch - gridPx) / 2;
    requestDraw();
  }, [requestDraw]);

  const zoomAt = useCallback((factor, px, py) => {
    const canvas = canvasRef.current;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (px == null) { px = cw / 2; py = ch / 2; }
    const oldC = cellSize();
    const worldX = (px - view.current.tx) / oldC;
    const worldY = (py - view.current.ty) / oldC;
    view.current.scale = clamp(view.current.scale * factor, 1, 26);
    const newC = cellSize();
    view.current.tx = px - worldX * newC;
    view.current.ty = py - worldY * newC;
    clampPan();
    requestDraw();
  }, [requestDraw]);

  useImperativeHandle(ref, () => ({
    zoomToPixel(idx) {
      const g = propsRef.current.gridSize;
      const canvas = canvasRef.current;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      view.current.scale = clamp(10, 1, 26);
      const c = cellSize();
      const x = idx % g;
      const y = Math.floor(idx / g);
      view.current.tx = cw / 2 - (x + 0.5) * c;
      view.current.ty = ch / 2 - (y + 0.5) * c;
      clampPan();
      requestDraw();
    },
  }));

  // setup
  useEffect(() => {
    const img = new Image();
    img.onload = () => requestDraw();
    img.src = WALL_TEXTURE;
    bgImg.current = img;
    fitView();
    const ro = new ResizeObserver(() => { clampPan(); requestDraw(); });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { requestDraw(); }, [soldSet, selected, tool, requestDraw]);

  // pointer handlers
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX - rect.left, e.clientY - rect.top);
    };

    const onDown = (e) => {
      canvas.setPointerCapture(e.pointerId);
      const { cx, cy, mx, my } = cellAt(e.clientX, e.clientY);
      const isPan = propsRef.current.tool === "pan" || e.button === 2 || e.button === 1;
      drag.current = {
        mode: isPan ? "pan" : "select",
        startX: mx, startY: my,
        lastX: e.clientX, lastY: e.clientY,
        startCell: { cx, cy }, curCell: { cx, cy },
        moved: false,
      };
    };

    const onMove = (e) => {
      const { cx, cy, mx, my } = cellAt(e.clientX, e.clientY);
      hoverCell.current = { cx, cy, mx, my };
      const g = propsRef.current.gridSize;
      if (drag.current) {
        const dx = e.clientX - drag.current.lastX;
        const dy = e.clientY - drag.current.lastY;
        if (Math.abs(mx - drag.current.startX) > 4 || Math.abs(my - drag.current.startY) > 4)
          drag.current.moved = true;
        if (drag.current.mode === "pan") {
          view.current.tx += dx;
          view.current.ty += dy;
          drag.current.lastX = e.clientX;
          drag.current.lastY = e.clientY;
          clampPan();
        } else {
          drag.current.curCell = { cx: clamp(cx, 0, g - 1), cy: clamp(cy, 0, g - 1) };
        }
        propsRef.current.onHover(null);
        requestDraw();
        return;
      }
      // hover tooltip
      const idx = cy * g + cx;
      if (cx >= 0 && cx < g && cy >= 0 && cy < g && propsRef.current.soldSet.has(idx)) {
        const data = propsRef.current.soldMap.get(idx);
        propsRef.current.onHover({ data, x: mx, y: my });
      } else {
        propsRef.current.onHover(null);
      }
      requestDraw();
    };

    const finish = (e) => {
      if (!drag.current) return;
      const d = drag.current;
      const g = propsRef.current.gridSize;
      if (d.mode === "select") {
        if (!d.moved) {
          const idx = d.startCell.cy * g + d.startCell.cx;
          if (d.startCell.cx >= 0 && d.startCell.cx < g && d.startCell.cy >= 0 && d.startCell.cy < g)
            propsRef.current.onToggle(idx);
        } else {
          const a = d.startCell, b = d.curCell;
          const list = [];
          for (let y = Math.min(a.cy, b.cy); y <= Math.max(a.cy, b.cy); y++)
            for (let x = Math.min(a.cx, b.cx); x <= Math.max(a.cx, b.cx); x++)
              if (x >= 0 && x < g && y >= 0 && y < g) list.push(y * g + x);
          propsRef.current.onBoxSelect(list);
        }
      }
      drag.current = null;
      requestDraw();
    };

    const onLeave = () => { hoverCell.current = null; propsRef.current.onHover(null); requestDraw(); };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", finish);
    canvas.addEventListener("pointercancel", finish);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", finish);
      canvas.removeEventListener("pointercancel", finish);
      canvas.removeEventListener("pointerleave", onLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomAt, requestDraw]);

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        data-testid="pixel-wall-canvas"
        className="w-full h-full rounded-lg touch-none"
        style={{ cursor: tool === "pan" ? "grab" : "crosshair", display: "block" }}
      />
      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <button data-testid="zoom-in-button" onClick={() => zoomAt(1.4)}
          className="w-10 h-10 grid place-items-center rounded-lg bg-slate-950/70 backdrop-blur border border-rose-900/40 text-rose-200 hover:bg-rose-600 hover:text-white transition-colors">
          <ZoomIn size={18} />
        </button>
        <button data-testid="zoom-out-button" onClick={() => zoomAt(1 / 1.4)}
          className="w-10 h-10 grid place-items-center rounded-lg bg-slate-950/70 backdrop-blur border border-rose-900/40 text-rose-200 hover:bg-rose-600 hover:text-white transition-colors">
          <ZoomOut size={18} />
        </button>
        <button data-testid="fit-view-button" onClick={fitView}
          className="w-10 h-10 grid place-items-center rounded-lg bg-slate-950/70 backdrop-blur border border-rose-900/40 text-rose-200 hover:bg-rose-600 hover:text-white transition-colors">
          <Maximize2 size={16} />
        </button>
      </div>
    </div>
  );
});

export default WallCanvas;
